import ApiBase from 'apis';
import config from 'config';
import { withPagination } from 'apis/utils';
import { PaginatedResult, PaginationOptions } from 'types/api';
import {
  Comment,
  CommentId,
  CommunityUpdate,
  CompanyUpdate,
  ContentCreateDTO,
  ContentUpdateDTO,
  CreateCommunityUpdateDTO,
  Like,
} from 'types/content';
import { DocumentDownloadDTO, DocumentList } from 'types/documents';
import { ICwUserId } from 'types/user';

const api = ApiBase();

// There are two endpoints for updates, one for company updates and one for room updates
// Main reason is that they have completely different access control
// In order for both room updates and company updates to be rendered the same way, they pass in which api methods they use
export interface UpdateAPI {
  togglePin: (contentId: string, isPinned: boolean) => Promise<void>;
  delete: (contentId: string) => Promise<void>;
  attachments: {
    list: (contentId: string) => Promise<DocumentList>;
    download: (updateId: string, fileId: string) => Promise<DocumentDownloadDTO>;
  };
  likes: {
    list: (contentId: string, paginationOptions?: PaginationOptions) => Promise<PaginatedResult<ICwUserId>>;
    like: (contentId: string) => Promise<void>;
    unLike: (contentId: string) => Promise<void>;
  };
  comments: {
    list: (contentId: string, paginationOptions?: PaginationOptions) => Promise<PaginatedResult<Comment>>;
    getReplies: (
      contentId: string,
      commentId: string,
      paginationOptions?: PaginationOptions,
    ) => Promise<PaginatedResult<Comment>>;
    post: (contentId: string, comment: string) => Promise<Comment>;
    replyToComment: (contentId: string, commentId: string, comment: string) => Promise<Comment>;
    delete: (contentId: string, commentId: CommentId) => Promise<void>;
  };
}

const baseUrl = `${config.COMPANIES_API_URL}/updates`;

const singleUpdateAPI = {
  getContext: (updateId: string) => `${baseUrl}/${updateId}/context`,
  attachments: {
    list: (contentId: string) => api.get<DocumentList>(`${baseUrl}/${contentId}/attachments`),
  },
};

export const ContentAPIUrls = {
  listTopics: (communityId: string | number) => `${config.COMPANIES_API_URL}/communities/${communityId}/content-topics`,
  createTopic: (communityId: string | number) =>
    `${config.COMPANIES_API_URL}/communities/${communityId}/content-topics`,
  updateTopic: (communityId: string | number, topicId: string) =>
    `${config.COMPANIES_API_URL}/communities/${communityId}/content-topics/${topicId}`,
  deleteTopic: (communityId: string | number, topicId: string) =>
    `${config.COMPANIES_API_URL}/communities/${communityId}/content-topics/${topicId}`,
  reassignTopic: (communityId: string | number, originalTopicId: string) =>
    `${config.COMPANIES_API_URL}/communities/${communityId}/content-topics/${originalTopicId}/reassign`,
  getPostCountPerTopic: (communityId: string | number, topicId: string) =>
    `${config.COMPANIES_API_URL}/communities/${communityId}/content-topics/${topicId}/count`,
  getUpdateForEmbed: (updateId: string) => `${config.COMPANIES_API_URL}/updates/${updateId}/embed`,
};

interface CompanyUpdateAPI extends UpdateAPI {
  get: (updateId: string) => string;
  list: (isArchived: boolean, paginationOptions: PaginationOptions) => Promise<PaginatedResult<CompanyUpdate>>;
  create: (dto: ContentCreateDTO) => Promise<CompanyUpdate>;
  editUpdate: (updateId: string, dto: ContentUpdateDTO) => Promise<CompanyUpdate>;
}

export const companyUpdateAPIurls = {
  list: (companyId: number, isArchived: boolean, paginationOptions: PaginationOptions) =>
    withPagination(`${config.COMPANIES_API_URL}/companies/${companyId}/updates`, paginationOptions, {
      archived: isArchived,
    }),
};

export const companyUpdateAPI = (companyId: number): CompanyUpdateAPI => {
  const baseUrl = `${config.COMPANIES_API_URL}/companies/${companyId}`;

  return {
    get: (updateId: string) => `${baseUrl}/updates/${updateId}`,
    list: (isArchived: boolean, paginationOptions: PaginationOptions) =>
      api.get<PaginatedResult<CompanyUpdate>>(`${baseUrl}/updates`, {
        requestParams: { ...paginationOptions, archived: isArchived },
      }),
    create: (dto: ContentCreateDTO) => api.post<CompanyUpdate>(`${baseUrl}/updates`, dto),
    editUpdate: (updateId: UUID, dto: ContentUpdateDTO) =>
      api.patch<CompanyUpdate>(`${baseUrl}/updates/${updateId}`, dto),
    togglePin: (updateId: UUID, isPinned: boolean) => api.patch<void>(`${baseUrl}/updates/${updateId}`, { isPinned }),
    delete: (updateId: UUID) => api.deleteRequest(`${baseUrl}/updates/${updateId}`),
    likes: {
      list: (updateId: string, paginationOptions?: PaginationOptions) =>
        api.get<PaginatedResult<Like>>(withPagination(`${baseUrl}/updates/${updateId}/likes`, paginationOptions)),
      like: (updateId: string) => api.post<void>(`${baseUrl}/updates/${updateId}/likes`, {}),
      unLike: (updateId: string) => api.deleteRequest(`${baseUrl}/updates/${updateId}/likes`),
    },

    attachments: {
      download: (updateId, fileId) =>
        api.get<DocumentDownloadDTO>(`${baseUrl}/updates/${updateId}/attachments/${fileId}`),
      list: (updateId: string) => api.get<DocumentList>(`${baseUrl}/updates/${updateId}/attachments`),
    },
    comments: {
      list: (contentId: string, paginationOptions?: PaginationOptions): Promise<PaginatedResult<Comment>> =>
        api.get<PaginatedResult<Comment>>(
          withPagination(`${baseUrl}/updates/${contentId}/comments`, paginationOptions),
        ),
      getReplies: (updateId: string, commentId: string, paginationOptions?: PaginationOptions) =>
        api.get<PaginatedResult<Comment>>(
          withPagination(`${baseUrl}/updates/${updateId}/comments/${commentId}/replies`, paginationOptions),
        ),
      post: (contentId: string, comment: string) =>
        api.post<Comment>(`${baseUrl}/updates/${contentId}/comments`, { content: comment }),
      replyToComment: (updateId: string, commentId: string, comment: string) =>
        api.post<Comment>(`${baseUrl}/updates/${updateId}/comments`, { content: comment, responseTo: commentId }),
      delete: (updateId: string, commentId: CommentId) =>
        api.deleteRequest(`${baseUrl}/updates/${updateId}/comments/${commentId}`),
    },
  };
};

interface CommunityAPI extends UpdateAPI {
  create: (dto: CreateCommunityUpdateDTO) => Promise<CommunityUpdate>;
  editUpdate: (updateId: string, dto: ContentUpdateDTO) => Promise<CommunityUpdate>;
  get: (updateId: string) => string;
}

export const communityUpdateAPI = (communityId: number): CommunityAPI => {
  const baseUrl = `${config.COMPANIES_API_URL}/communities/${communityId}`;

  return {
    create: (dto: CreateCommunityUpdateDTO) => api.post<CommunityUpdate>(`${baseUrl}/updates`, dto),
    delete: (updateId: UUID) => api.deleteRequest(`${baseUrl}/updates/${updateId}`),
    editUpdate: (updateId: UUID, dto: ContentUpdateDTO) =>
      api.patch<CommunityUpdate>(`${baseUrl}/updates/${updateId}`, dto),
    get: (updateId: string) => `${baseUrl}/updates/${updateId}`,
    togglePin: (updateId: UUID, isPinned: boolean) => api.patch<void>(`${baseUrl}/updates/${updateId}`, { isPinned }),
    likes: {
      list: (updateId: string, paginationOptions?: PaginationOptions) =>
        api.get<PaginatedResult<Like>>(withPagination(`${baseUrl}/updates/${updateId}/likes`, paginationOptions)),
      like: (updateId: string) => api.post<void>(`${baseUrl}/updates/${updateId}/likes`, {}),
      unLike: (updateId: string) => api.deleteRequest(`${baseUrl}/updates/${updateId}/likes`),
    },
    attachments: {
      download: (updateId, fileId) =>
        api.get<DocumentDownloadDTO>(`${baseUrl}/updates/${updateId}/attachments/${fileId}`),
      list: (updateId: string) => api.get<DocumentList>(`${baseUrl}/updates/${updateId}/attachments`),
    },
    comments: {
      list: (contentId: string, paginationOptions?: PaginationOptions): Promise<PaginatedResult<Comment>> =>
        api.get<PaginatedResult<Comment>>(
          withPagination(`${baseUrl}/updates/${contentId}/comments`, paginationOptions),
        ),
      getReplies: (updateId: string, commentId: string, paginationOptions?: PaginationOptions) =>
        api.get<PaginatedResult<Comment>>(
          withPagination(`${baseUrl}/updates/${updateId}/comments/${commentId}/replies`, paginationOptions),
        ),
      post: (contentId: string, comment: string) =>
        api.post<Comment>(`${baseUrl}/updates/${contentId}/comments`, {
          content: comment,
        }),
      replyToComment: (updateId: string, commentId: string, comment: string) =>
        api.post<Comment>(`${baseUrl}/updates/${updateId}/comments`, {
          content: comment,
          responseTo: commentId,
        }),
      delete: (updateId: string, commentId: CommentId) =>
        api.deleteRequest(`${baseUrl}/updates/${updateId}/comments/${commentId}`),
    },
  };
};

export default singleUpdateAPI;
